import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    generatePath,
    useHistory,
    useLocation,
    useParams,
} from 'react-router-dom';
import {
    getChatSessionDetails,
    getSessionContent,
} from 'flow/actions/chat-actions';
import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import Loading from 'flow/components/ui-components/Loading';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useChatMembers from 'flow/hooks/useChatMembers';
import { selectOwnChatUserKey } from 'flow/selectors/own-extension-selectors';
import {
    selectChatSessionAdmins,
    selectChatSessionIsLoaded,
} from 'flow/selectors/chat-selectors';
import { State } from 'flow/types/State';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { LocationType } from 'flow/types/Location';
import { routes } from 'flow/types/routes';
import MessagesMember from './messages/MessagesMember';

const MessagesMembersPage = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    const { pathname } = useLocation();
    const { openModal } = useModal();
    const { id: chatSessionKey } = useParams<{ id: ChatSessionKey }>();
    const { chatMembers, canAddMember } = useChatMembers(chatSessionKey);
    const ownChatUserKey = useAppSelector(selectOwnChatUserKey);
    const admins = useAppSelector((state: State) =>
        selectChatSessionAdmins(state, chatSessionKey),
    );
    const sessionIsLoaded = useAppSelector((state: State) =>
        selectChatSessionIsLoaded(state, chatSessionKey),
    );

    useEffect(() => {
        dispatch(getChatSessionDetails(chatSessionKey));
        dispatch(getSessionContent(chatSessionKey, 'posts'));
    }, [dispatch, chatSessionKey]);

    if (!sessionIsLoaded) {
        return <Loading />;
    }

    const lazyLoadAvatars = chatMembers.size > 10;

    return (
        <Page
            mainId="messages-members"
            header={`${i18next.t('chatsettings.members')} (${chatMembers.size})`}
            onClickBack={() =>
                history.push(
                    history.location.state?.from
                        ? history.location.state?.from
                        : generatePath(routes['messages.id.settings'], {
                              id: chatSessionKey,
                          }),
                    { from: pathname },
                )
            }
            action={
                canAddMember ? (
                    <Button
                        iconKey="person_add"
                        onClick={() =>
                            openModal('addMessageMember', {
                                id: chatSessionKey,
                            })
                        }
                    />
                ) : null
            }
        >
            {chatMembers.map((member) => (
                <MessagesMember
                    key={member.get('key')}
                    member={member}
                    isAdmin={admins.includes(member.get('key'))}
                    isOwnMember={member.get('key') === ownChatUserKey}
                    lazyLoadAvatar={lazyLoadAvatars}
                />
            ))}
        </Page>
    );
};

export default MessagesMembersPage;
